/**
 * エディタの幾何演算 (正規化座標 0〜1)。DOM に依存しない純粋関数にしてテストする。
 */
import { BANNER_LAYOUT_ROLES, type BannerLayoutKind } from './roles';
import { BANNER_LAYOUT_MAX_ROTATION, BANNER_LAYOUT_MIN_SIZE, type BannerLayout, type BannerLayoutBackground, type BannerLayoutElement } from './types';

export const SNAP_TOLERANCE = 0.01;

export type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
export const RESIZE_HANDLES: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

export type Rect = { x: number; y: number; w: number; h: number };

let idCounter = 0;
export function newElementId(): string {
  idCounter += 1;
  return `el_${Date.now().toString(36)}_${idCounter.toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const round4 = (v: number) => Math.round(v * 10000) / 10000;

export function normalizeRect<T extends Rect>(el: T): T {
  const w = clamp(el.w, BANNER_LAYOUT_MIN_SIZE, 1);
  const h = clamp(el.h, BANNER_LAYOUT_MIN_SIZE, 1);
  return { ...el, w: round4(w), h: round4(h), x: round4(clamp(el.x, 0, 1 - w)), y: round4(clamp(el.y, 0, 1 - h)) };
}

/** パレットから追加: 既定サイズでキャンバス中央に置く。同種がすでに中央にあれば少しずらす */
export function createElement(kind: BannerLayoutKind, existing: BannerLayoutElement[]): BannerLayoutElement {
  const role = BANNER_LAYOUT_ROLES[kind];
  let x = (1 - role.defaultW) / 2;
  let y = (1 - role.defaultH) / 2;
  const offset = existing.filter((e) => Math.abs(e.x - x) < 0.005 && Math.abs(e.y - y) < 0.005).length;
  x += offset * 0.03;
  y += offset * 0.03;
  const el: BannerLayoutElement = { id: newElementId(), kind, x, y, w: role.defaultW, h: role.defaultH };
  return normalizeRect(el);
}

export function moveRect<T extends Rect>(el: T, dx: number, dy: number): T {
  return normalizeRect({ ...el, x: el.x + dx, y: el.y + dy });
}

/** ハンドルで大きさを変える。反対側の辺は固定し、最小サイズとキャンバス内に収める */
export function resizeRect<T extends Rect>(el: T, handle: ResizeHandle, dx: number, dy: number): T {
  let left = el.x;
  let top = el.y;
  let right = el.x + el.w;
  let bottom = el.y + el.h;
  if (handle.includes('w')) left = clamp(left + dx, 0, right - BANNER_LAYOUT_MIN_SIZE);
  if (handle.includes('e')) right = clamp(right + dx, left + BANNER_LAYOUT_MIN_SIZE, 1);
  if (handle.includes('n')) top = clamp(top + dy, 0, bottom - BANNER_LAYOUT_MIN_SIZE);
  if (handle.includes('s')) bottom = clamp(bottom + dy, top + BANNER_LAYOUT_MIN_SIZE, 1);
  return normalizeRect({ ...el, x: left, y: top, w: right - left, h: bottom - top });
}

export type SnapGuides = { x: number[]; y: number[] };

function snapAxis(edges: number[], targets: number[], tol: number): { delta: number; at: number | null } {
  let best = { delta: 0, at: null as number | null, dist: tol + 1e-9 };
  for (const edge of edges) {
    for (const t of targets) {
      const d = Math.abs(t - edge);
      if (d <= best.dist) best = { delta: t - edge, at: t, dist: d };
    }
  }
  return { delta: best.at === null ? 0 : best.delta, at: best.at };
}

/** 移動中の要素を端 / 中央 / 他要素の辺に ±tol で吸着させる。吸着したガイド線の位置も返す */
export function snapMove<T extends Rect>(el: T, others: Rect[], tol = SNAP_TOLERANCE): { rect: T; guides: SnapGuides } {
  const xTargets = [0, 0.5, 1, ...others.flatMap((o) => [o.x, o.x + o.w / 2, o.x + o.w])];
  const yTargets = [0, 0.5, 1, ...others.flatMap((o) => [o.y, o.y + o.h / 2, o.y + o.h])];
  const sx = snapAxis([el.x, el.x + el.w / 2, el.x + el.w], xTargets, tol);
  const sy = snapAxis([el.y, el.y + el.h / 2, el.y + el.h], yTargets, tol);
  const rect = normalizeRect({ ...el, x: el.x + sx.delta, y: el.y + sy.delta });
  return { rect, guides: { x: sx.at === null ? [] : [sx.at], y: sy.at === null ? [] : [sy.at] } };
}

/** リサイズ中に動いている辺だけを吸着させる */
export function snapResize<T extends Rect>(el: T, handle: ResizeHandle, others: Rect[], tol = SNAP_TOLERANCE): { rect: T; guides: SnapGuides } {
  const xTargets = [0, 0.5, 1, ...others.flatMap((o) => [o.x, o.x + o.w / 2, o.x + o.w])];
  const yTargets = [0, 0.5, 1, ...others.flatMap((o) => [o.y, o.y + o.h / 2, o.y + o.h])];
  const guides: SnapGuides = { x: [], y: [] };
  let rect = el;
  if (handle.includes('e') || handle.includes('w')) {
    const edge = handle.includes('e') ? el.x + el.w : el.x;
    const s = snapAxis([edge], xTargets, tol);
    if (s.at !== null) {
      rect = resizeRect(rect, handle.includes('e') ? 'e' : 'w', s.delta, 0);
      guides.x.push(s.at);
    }
  }
  if (handle.includes('n') || handle.includes('s')) {
    const edge = handle.includes('s') ? el.y + el.h : el.y;
    const s = snapAxis([edge], yTargets, tol);
    if (s.at !== null) {
      rect = resizeRect(rect, handle.includes('s') ? 's' : 'n', 0, s.delta);
      guides.y.push(s.at);
    }
  }
  return { rect, guides };
}

/**
 * 傾いた要素の外接矩形 (正規化座標)。回転は画面の px で起きるので、キャンバスの比率 (W/H) で縦横を戻して計算する。
 */
export function rotatedBounds(el: Rect & { rotation?: number }, ratio = 1): Rect {
  const deg = el.rotation ?? 0;
  if (!deg) return { x: el.x, y: el.y, w: el.w, h: el.h };
  const rad = (deg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  // px 単位 (高さ=1, 幅=ratio) の半幅・半高
  const hw = (el.w * ratio) / 2;
  const hh = el.h / 2;
  const bw = (hw * cos + hh * sin) / ratio;
  const bh = hw * sin + hh * cos;
  const cx = el.x + el.w / 2;
  const cy = el.y + el.h / 2;
  return { x: cx - bw, y: cy - bh, w: bw * 2, h: bh * 2 };
}

/**
 * 背景の分割の写真側の多角形 (px)。backend/services/banner_layout/schema.py split_visual_polygon と同じ式。
 */
export function splitVisualPolygon(bg: BannerLayoutBackground, width: number, height: number): [number, number][] {
  const rad = ((bg.angle ?? 0) * Math.PI) / 180;
  let px: number;
  let py: number;
  let dx: number;
  let dy: number;
  let keepNegative: boolean;
  if (bg.orientation === 'vertical') {
    px = bg.position * width;
    py = height / 2;
    dx = -Math.sin(rad);
    dy = Math.cos(rad);
    keepNegative = bg.visualSide === 'right';
  } else {
    px = width / 2;
    py = bg.position * height;
    dx = Math.cos(rad);
    dy = Math.sin(rad);
    keepNegative = bg.visualSide === 'top';
  }
  const side = (q: [number, number]) => dx * (q[1] - py) - dy * (q[0] - px);
  const inside = (v: number) => (keepNegative ? v <= 0 : v >= 0);
  const corners: [number, number][] = [[0, 0], [width, 0], [width, height], [0, height]];
  const out: [number, number][] = [];
  corners.forEach((cur, i) => {
    const prev = corners[(i + corners.length - 1) % corners.length];
    const sc = side(cur);
    const sp = side(prev);
    const cross = (): [number, number] => {
      const t = sp / (sp - sc);
      return [prev[0] + (cur[0] - prev[0]) * t, prev[1] + (cur[1] - prev[1]) * t];
    };
    if (inside(sc)) {
      if (!inside(sp)) out.push(cross());
      out.push(cur);
    } else if (inside(sp)) {
      out.push(cross());
    }
  });
  return out;
}

/**
 * 背景の分割線がキャンバスの端と交わる2点 (px)。splitVisualPolygon と同じ線。backend schema.py split_line と同じ式。
 */
export function splitLine(bg: BannerLayoutBackground, width: number, height: number): [number, number][] {
  const rad = ((bg.angle ?? 0) * Math.PI) / 180;
  const vertical = bg.orientation === 'vertical';
  const px = vertical ? bg.position * width : width / 2;
  const py = vertical ? height / 2 : bg.position * height;
  const dx = vertical ? -Math.sin(rad) : Math.cos(rad);
  const dy = vertical ? Math.cos(rad) : Math.sin(rad);
  let t0 = -1e9;
  let t1 = 1e9;
  for (const [p0, d, hi] of [[px, dx, width], [py, dy, height]] as const) {
    if (Math.abs(d) < 1e-12) {
      if (p0 < 0 || p0 > hi) return [];
      continue;
    }
    const a = (0 - p0) / d;
    const b = (hi - p0) / d;
    t0 = Math.max(t0, Math.min(a, b));
    t1 = Math.min(t1, Math.max(a, b));
  }
  if (t0 > t1) return [];
  return [[px + dx * t0, py + dy * t0], [px + dx * t1, py + dy * t1]];
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  const e = 1e-6;
  return a.x < b.x + b.w - e && b.x < a.x + a.w - e && a.y < b.y + b.h - e && b.y < a.y + a.h - e;
}

/** 傾いた要素の4隅 (px 単位: 幅=ratio・高さ=1)。回転は画面の px で起きるので比率を掛けてから回す */
export function rotatedCorners(el: Rect & { rotation?: number }, ratio = 1): [number, number][] {
  const rad = ((el.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const cx = (el.x + el.w / 2) * ratio;
  const cy = el.y + el.h / 2;
  const hw = (el.w * ratio) / 2;
  const hh = el.h / 2;
  return ([[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]] as [number, number][]).map(([dx, dy]) => [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos]);
}

/** 凸多角形どうしが面で重なるか (分離軸定理)。辺で接しているだけなら重ならない扱い */
export function polygonsOverlap(a: [number, number][], b: [number, number][]): boolean {
  const e = 1e-6;
  for (const poly of [a, b]) {
    for (let i = 0; i < poly.length; i += 1) {
      const [x1, y1] = poly[i];
      const [x2, y2] = poly[(i + 1) % poly.length];
      const nx = y1 - y2;
      const ny = x2 - x1;
      const project = (pts: [number, number][]) => {
        let min = Infinity;
        let max = -Infinity;
        for (const [px, py] of pts) {
          const d = px * nx + py * ny;
          if (d < min) min = d;
          if (d > max) max = d;
        }
        return [min, max];
      };
      const [amin, amax] = project(a);
      const [bmin, bmax] = project(b);
      if (amax <= bmin + e || bmax <= amin + e) return false;
    }
  }
  return true;
}

/** 他の要素と重なっている要素の id。傾いた要素は回転した矩形そのもの (外接矩形ではない) で判定する。ratio はキャンバスの W/H.
 *  斜め割りのように傾いた枠を縦に並べると外接矩形は重なるが実際の枠は重ならないため、分離軸定理で判定する (2026-10-18) */
export function overlappingIds(elements: BannerLayoutElement[], ratio = 1): Set<string> {
  const polygons = elements.map((e) => rotatedCorners(e, ratio));
  const ids = new Set<string>();
  for (let i = 0; i < elements.length; i += 1) {
    for (let j = i + 1; j < elements.length; j += 1) {
      if (polygonsOverlap(polygons[i], polygons[j])) {
        ids.add(elements[i].id);
        ids.add(elements[j].id);
      }
    }
  }
  return ids;
}

/** プリセット / テンプレートの複製: id を振り直す (元のプリセットは変えない) */
export function cloneLayoutWithNewIds(layout: BannerLayout, aspectRatio?: string): BannerLayout {
  return {
    version: 1,
    aspectRatio: aspectRatio ?? layout.aspectRatio,
    elements: layout.elements.map((el) => ({ ...el, id: newElementId() })),
    ...(layout.background ? { background: { ...layout.background } } : {}),
  };
}

/* ---- 背景の分割線のドラッグ操作 (§20・2026-10-19) ---- */

/** 分割線の位置が中心 (0.5) に吸着する幅 (正規化座標) */
export const SPLIT_POSITION_SNAP = 0.015;
/** 分割線の傾きが 0 度 (まっすぐ) に吸着する幅 (度) */
export const SPLIT_ANGLE_SNAP_DEG = 3;

/**
 * 分割線そのものを掴んで平行移動する。縦割りは横の移動量、横割りは縦の移動量 (いずれも正規化) だけ position が動く
 * (線を平行に動かすと中心線との交点も同じだけ動く)。snap のときは中心 (0.5) に吸着する
 */
export function dragSplitPosition(
  origin: BannerLayoutBackground,
  dxNorm: number,
  dyNorm: number,
  snap = true,
): { background: BannerLayoutBackground; snapped: boolean } {
  const delta = origin.orientation === 'vertical' ? dxNorm : dyNorm;
  let position = clamp(origin.position + delta, 0, 1);
  let snapped = false;
  if (snap && Math.abs(position - 0.5) <= SPLIT_POSITION_SNAP) {
    position = 0.5;
    snapped = true;
  }
  return { background: { ...origin, position: round4(position) }, snapped };
}

/**
 * 分割線の端を掴んで回す。軸は分割線が中心線と交わる点 (縦割り (position×W, H/2)・横割り (W/2, position×H)) で、
 * 軸からポインタ (px) への向きを分割線の向きにする。end は splitLine が返す端点の何番目か (0 = 軸から見て逆向きの端)。
 * 傾きは -45〜45 度・1度刻みに収め、snap のときは 0 度の近くで 0 に吸着する
 */
export function dragSplitAngle(
  origin: BannerLayoutBackground,
  pointer: [number, number],
  width: number,
  height: number,
  end: 0 | 1,
  snap = true,
): { background: BannerLayoutBackground; snapped: boolean } {
  const vertical = origin.orientation === 'vertical';
  const px = vertical ? origin.position * width : width / 2;
  const py = vertical ? height / 2 : origin.position * height;
  const sign = end === 1 ? 1 : -1;
  const vx = (pointer[0] - px) * sign;
  const vy = (pointer[1] - py) * sign;
  if (Math.abs(vx) < 1e-9 && Math.abs(vy) < 1e-9) return { background: origin, snapped: false };
  // 縦割りの向きは (-sin a, cos a)、横割りは (cos a, sin a)
  const rad = vertical ? Math.atan2(-vx, vy) : Math.atan2(vy, vx);
  let deg = Math.round(clamp((rad * 180) / Math.PI, -BANNER_LAYOUT_MAX_ROTATION, BANNER_LAYOUT_MAX_ROTATION));
  let snapped = false;
  if (snap && Math.abs(deg) <= SPLIT_ANGLE_SNAP_DEG) {
    deg = 0;
    snapped = true;
  }
  return { background: { ...origin, angle: deg }, snapped };
}

/** 多角形の重心 (面積加重)。退化していれば頂点の平均 */
export function polygonCentroid(points: [number, number][]): [number, number] {
  if (points.length === 0) return [0, 0];
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < points.length; i += 1) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[(i + 1) % points.length];
    const cross = x0 * y1 - x1 * y0;
    area += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  if (Math.abs(area) < 1e-9) {
    return [points.reduce((s, p) => s + p[0], 0) / points.length, points.reduce((s, p) => s + p[1], 0) / points.length];
  }
  return [cx / (3 * area), cy / (3 * area)];
}

/** 背景の分割の、写真側と文字側それぞれの領域の重心 (px)。キャンバスに「写真」「文字」の目印を置く位置 */
export function splitSideCentroids(bg: BannerLayoutBackground, width: number, height: number): { visual: [number, number]; text: [number, number] } {
  const opposite: Record<BannerLayoutBackground['visualSide'], BannerLayoutBackground['visualSide']> = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' };
  const textSide = { ...bg, visualSide: opposite[bg.visualSide] } as BannerLayoutBackground;
  return {
    visual: polygonCentroid(splitVisualPolygon(bg, width, height)),
    text: polygonCentroid(splitVisualPolygon(textSide, width, height)),
  };
}

/* ---- 要素の回転つまみ (§21・2026-10-19) ---- */

/** 要素の傾きを置き換える。0 なら rotation キーごと落とす (0 は持たない約束・§16) */
export function withRotation(element: BannerLayoutElement, deg: number): BannerLayoutElement {
  const rest: BannerLayoutElement = { ...element };
  delete rest.rotation;
  return deg ? { ...rest, rotation: deg } : rest;
}

/** 要素の傾きが 0 度に吸着する幅 (度) */
export const ELEMENT_ANGLE_SNAP_DEG = 3;

/** center から pointer への向き (度・画面座標・時計回りが正)。重なっていれば null */
function bearingDeg(center: [number, number], pointer: [number, number]): number | null {
  const vx = pointer[0] - center[0];
  const vy = pointer[1] - center[1];
  if (Math.abs(vx) < 1e-9 && Math.abs(vy) < 1e-9) return null;
  return (Math.atan2(vy, vx) * 180) / Math.PI;
}

/**
 * 要素の回転つまみを掴んで回す。掴んだ位置 (startPointer) からポインタ (pointer) まで、要素の中心 (center・px) の周りに
 * 回った角度を元の傾き (originRotation) に足す (つまみがどこに付いていても、掴んだ瞬間に跳ばない)。
 * -45〜45 度・1度刻みに収め、snap のときは 0 度の近くで 0 に吸着する
 */
export function dragElementRotation(
  center: [number, number],
  startPointer: [number, number],
  pointer: [number, number],
  originRotation: number,
  snap = true,
): { rotation: number; snapped: boolean } {
  const from = bearingDeg(center, startPointer);
  const to = bearingDeg(center, pointer);
  if (from === null || to === null) return { rotation: originRotation, snapped: false };
  // 回った角度を -180〜180 に正規化してから足す
  let delta = to - from;
  delta = ((((delta + 180) % 360) + 360) % 360) - 180;
  let rotation = Math.round(clamp(originRotation + delta, -BANNER_LAYOUT_MAX_ROTATION, BANNER_LAYOUT_MAX_ROTATION));
  let snapped = false;
  if (snap && Math.abs(rotation) <= ELEMENT_ANGLE_SNAP_DEG) {
    rotation = 0;
    snapped = true;
  }
  return { rotation, snapped };
}
